import React from 'react';
import './HowItWorks.css';

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "Upload Your Files",
      description: "Drag and drop your files or click to browse. Select multiple files for batch processing.",
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64">
          <defs>
            <linearGradient id="step1Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#667eea" />
              <stop offset="100%" stopColor="#764ba2" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="30" fill="url(#step1Gradient)" fillOpacity="0.1" stroke="url(#step1Gradient)" strokeWidth="2"/>
          <path d="M32 20v20M24 28l8-8 8 8" stroke="url(#step1Gradient)" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="20" y="44" width="24" height="4" rx="2" fill="url(#step1Gradient)" fillOpacity="0.3"/>
        </svg>
      )
    },
    {
      number: "02", 
      title: "Choose Your Options",
      description: "Select merge mode to combine files into one PDF, or individual mode for separate conversions.",
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64">
          <defs>
            <linearGradient id="step2Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4facfe" />
              <stop offset="100%" stopColor="#00f2fe" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="30" fill="url(#step2Gradient)" fillOpacity="0.1" stroke="url(#step2Gradient)" strokeWidth="2"/>
          <rect x="18" y="20" width="12" height="16" rx="2" stroke="url(#step2Gradient)" strokeWidth="2" fill="none"/>
          <rect x="34" y="20" width="12" height="16" rx="2" stroke="url(#step2Gradient)" strokeWidth="2" fill="none"/>
          <path d="M24 44h16" stroke="url(#step2Gradient)" strokeWidth="3" strokeLinecap="round"/>
          <circle cx="20" cy="48" r="2" fill="url(#step2Gradient)"/>
          <circle cx="32" cy="48" r="2" fill="url(#step2Gradient)"/>
          <circle cx="44" cy="48" r="2" fill="url(#step2Gradient)"/>
        </svg>
      )
    },
    {
      number: "03",
      title: "Convert to PDF",
      description: "Click convert and watch the progress. Our advanced algorithms handle the conversion quickly.",
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64">
          <defs>
            <linearGradient id="step3Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fa709a" />
              <stop offset="100%" stopColor="#fee140" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="30" fill="url(#step3Gradient)" fillOpacity="0.1" stroke="url(#step3Gradient)" strokeWidth="2"/>
          <circle cx="32" cy="32" r="12" stroke="url(#step3Gradient)" strokeWidth="3" fill="none"/>
          <path d="M32 24v8l6 6" stroke="url(#step3Gradient)" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <circle cx="46" cy="18" r="4" fill="url(#step3Gradient)"/>
          <circle cx="18" cy="46" r="3" fill="url(#step3Gradient)" fillOpacity="0.6"/>
        </svg>
      )
    },
    {
      number: "04",
      title: "Download Results",
      description: "Your converted PDF files are automatically downloaded. All processing happens securely in your browser.",
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64">
          <defs>
            <linearGradient id="step4Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a8edea" />
              <stop offset="100%" stopColor="#fed6e3" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="30" fill="url(#step4Gradient)" fillOpacity="0.1" stroke="url(#step4Gradient)" strokeWidth="2"/>
          <path d="M32 44V24M24 36l8 8 8-8" stroke="url(#step4Gradient)" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="20" y="16" width="24" height="4" rx="2" fill="url(#step4Gradient)" fillOpacity="0.3"/>
        </svg>
      )
    }
  ];

  return (
    <section id="how-it-works" className="how-it-works">
      <div className="landing-container-full">
        <div className="section-header">
          <h2 className="section-title">How It Works</h2>
          <p className="section-description">
            Convert your files to PDF in just four simple steps
          </p>
        </div>

        <div className="steps-container">
          {steps.map((step, index) => (
            <div key={index} className="step">
              <div className="landing-step-content">
                <div className="step-icon">
                  {step.icon}
                </div>
                <div className="step-number">{step.number}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className="step-connector">
                  <svg width="100%" height="2">
                    <defs>
                      <linearGradient id="connectorGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#667eea" />
                        <stop offset="100%" stopColor="#764ba2" />
                      </linearGradient>
                    </defs>
                    <line x1="0" y1="1" x2="100%" y2="1" stroke="url(#connectorGradient)" strokeWidth="2" strokeDasharray="4,4">
                      <animate attributeName="stroke-dashoffset" values="0;8" dur="1s" repeatCount="indefinite"/>
                    </line>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="cta-section">
          <div className="cta-content">
            <h3>Ready to Convert Your Files?</h3>
            <p>It's free, fast, and secure. No registration required.</p>
            <button className="btn btn-primary btn-large" onClick={() => window.location.hash = '#converter'}>
              Try It Now
              <svg width="20" height="20" viewBox="0 0 20 20" className="btn-icon">
                <path d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;